import React, { useState, useMemo } from 'react';

const SYMBOLS = [
  { label: 'BTC/USD', value: 'BITSTAMP:BTCUSD' },
  { label: 'ETH/USD', value: 'BITSTAMP:ETHUSD' },
  { label: 'BNB/USD', value: 'BINANCE:BNBUSDT' },
  { label: 'SOL/USD', value: 'BINANCE:SOLUSDT' },
  { label: 'XRP/USD', value: 'BITSTAMP:XRPUSD' },
  { label: 'AAPL', value: 'NASDAQ:AAPL' },
  { label: 'TSLA', value: 'NASDAQ:TSLA' },
  { label: 'SPY', value: 'AMEX:SPY' },
  { label: 'EUR/USD', value: 'FX:EURUSD' },
  { label: 'GBP/USD', value: 'FX:GBPUSD' },
  { label: 'GOLD', value: 'TVC:GOLD' },
];

const INTERVALS = [
  { label: '1m', value: '1' },
  { label: '5m', value: '5' },
  { label: '15m', value: '15' },
  { label: '1H', value: '60' },
  { label: '4H', value: '240' },
  { label: '1D', value: 'D' },
  { label: '1W', value: 'W' },
];

const TradingViewChartsPage = () => {
  const [symbol, setSymbol] = useState('BITSTAMP:BTCUSD');
  const [chartInterval, setChartInterval] = useState('D');

  const iframeSrc = useMemo(() => {
    const config = {
      autosize: true,
      symbol: symbol,
      interval: chartInterval,
      timezone: 'Etc/UTC',
      theme: 'dark',
      style: '1',
      locale: 'en',
      backgroundColor: 'rgba(10, 10, 10, 1)',
      gridColor: 'rgba(255, 255, 255, 0.04)',
      hide_top_toolbar: false,
      hide_legend: false,
      allow_symbol_change: true,
      save_image: true,
      calendar: false,
      support_host: 'https://www.tradingview.com',
    };
    return `https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js?${encodeURIComponent(JSON.stringify(config))}`;
  }, [symbol, chartInterval]);

  const widgetUrl = useMemo(() => {
    const config = {
      autosize: true,
      symbol: symbol,
      interval: chartInterval,
      timezone: 'Etc/UTC',
      theme: 'dark',
      style: '1',
      locale: 'en',
      backgroundColor: 'rgba(10, 10, 10, 1)',
      gridColor: 'rgba(255, 255, 255, 0.04)',
      hide_top_toolbar: false,
      hide_legend: false,
      allow_symbol_change: true,
      save_image: true,
      calendar: false,
      support_host: 'https://www.tradingview.com',
    };
    return `https://www.tradingview-widget.com/embed-widget/advanced-chart/?locale=en#${encodeURIComponent(JSON.stringify(config))}`;
  }, [symbol, chartInterval]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl uppercase text-white mb-2" data-testid="tradingview-title">
              TradingView Charts
            </h1>
            <p className="text-[#8892B0]">Live charts powered by TradingView</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 mb-6">
          {/* Symbol Selector */}
          <div className="flex flex-wrap gap-1.5" data-testid="symbol-selector">
            {SYMBOLS.map((s) => (
              <button
                key={s.value}
                onClick={() => setSymbol(s.value)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  symbol === s.value
                    ? 'bg-[#007AFF] text-white'
                    : 'bg-[#131313] text-[#8892B0] border border-white/10 hover:text-white hover:border-white/30'
                }`}
                data-testid={`symbol-${s.label.replace('/', '-')}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Interval Selector */}
          <div className="flex gap-1.5 ml-auto" data-testid="interval-selector">
            {INTERVALS.map((i) => (
              <button
                key={i.value}
                onClick={() => setChartInterval(i.value)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  chartInterval === i.value
                    ? 'bg-[#34C759] text-black'
                    : 'bg-[#131313] text-[#8892B0] border border-white/10 hover:text-white hover:border-white/30'
                }`}
                data-testid={`interval-${i.label}`}
              >
                {i.label}
              </button>
            ))}
          </div>
        </div>

        {/* TradingView Chart - Direct iframe embed */}
        <div
          className="bg-[#131313] border border-white/5 overflow-hidden"
          style={{ height: 'calc(100vh - 260px)', minHeight: '500px' }}
          data-testid="tradingview-chart"
        >
          <iframe
            key={`${symbol}-${chartInterval}`}
            src={widgetUrl}
            title="TradingView Chart"
            style={{ width: '100%', height: '100%', border: 'none' }}
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

export default TradingViewChartsPage;
